import Image from 'next/image';
import { X } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { PeekGrid } from '../../primitives/PeekGrid';
import type { PaywallDialogData } from './types';

export interface PaywallDialogProps {
  data: PaywallDialogData;
  /** Fires on close-X click or "not now" click. */
  onClose: () => void;
}

function renderEmphasized(text: string): ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <em key={i}>{part}</em> : part));
}

function renderBoldedTitle(text: string): ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <b key={i}>{part}</b> : part));
}

function renderTrustText(text: string): ReactNode {
  const lines = text.split(/\n/);
  return lines.map((line, i) => (
    <span key={i}>
      {line}
      {i < lines.length - 1 ? <br /> : null}
    </span>
  ));
}

/**
 * Rich paywall dialog content. Top section shows a peek grid of
 * locked videos; bottom section is a two-column offer (value list
 * on the left, CTA on the right). Caller wraps this in a modal host
 * (e.g. the platform's Radix `Dialog`) — `PaywallDialog` is the
 * body, not the backdrop.
 */
export function PaywallDialog({ data, onClose }: PaywallDialogProps): ReactElement {
  const peekVideos = data.peekVideos ?? [];
  const hasPeek = peekVideos.length > 0;
  const valueLabel = data.valueLabel ?? 'What you unlock';

  return (
    <div className="tb-paywall" role="document">
      <button type="button" onClick={onClose} className="tb-paywall-close" aria-label="Close">
        <X />
      </button>
      <div className="tb-paywall-peek">
        <div className="tb-paywall-eyebrow">{data.eyebrow}</div>
        <h2 className="tb-paywall-headline">{renderEmphasized(data.headline)}</h2>
        <p className="tb-paywall-sub">{data.sub}</p>
        {hasPeek ? (
          <PeekGrid
            items={peekVideos}
            {...(data.overflowNote !== undefined ? { overflowNote: data.overflowNote } : {})}
          />
        ) : null}
      </div>
      <div className="tb-paywall-offer">
        <div className="tb-paywall-value-col">
          <div className="tb-paywall-vc-label">{valueLabel}</div>
          <ul className="tb-paywall-value-stack">
            {data.valueBullets.map((bullet, i) => (
              <li key={i}>
                <span className="tb-paywall-chk" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 12l5 5L20 6" />
                  </svg>
                </span>
                <div>
                  <b>{renderBoldedTitle(bullet.title)}</b>
                  {bullet.detail !== undefined ? (
                    <>
                      <br />
                      <span className="tb-paywall-value-sub">{bullet.detail}</span>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="tb-paywall-cta-col">
          {data.creatorTag !== undefined ? (
            <div className="tb-paywall-creator">
              <span
                className="tb-paywall-creator-avatar"
                style={data.creatorAvatarSrc !== undefined ? { position: 'relative' } : undefined}
                aria-hidden="true"
              >
                {data.creatorAvatarSrc !== undefined ? (
                  <Image
                    src={data.creatorAvatarSrc}
                    alt=""
                    fill
                    sizes="22px"
                    style={{ objectFit: 'cover' }}
                  />
                ) : null}
              </span>
              {data.creatorTag}
            </div>
          ) : null}
          <a className="tb-paywall-cta" href={data.ctaHref}>
            {data.ctaLabel}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
          {data.trustText !== undefined ? (
            <div className="tb-paywall-trust">{renderTrustText(data.trustText)}</div>
          ) : null}
          {data.notNowLabel !== undefined ? (
            <button type="button" onClick={onClose} className="tb-paywall-notnow">
              {data.notNowLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
